import type { EfteruddannelsesperiodeIndberetningOverviewModel } from "@/interfaces/efteruddannelsesperiodeIndberetningOverview";
import { formatDanishLongDate } from "@/util/date";
import { REQUIRED_PERIOD_LESSONS } from "@cle/constants/lessonRequirements";
import { formatLessons } from "@cle/utils/lessonLimits";

/** Vises hvor CLE ikke kunne svare — et 0 ville vaere direkte misvisende. */
export const LESSONS_UNAVAILABLE_LABEL = "kunne ikke hentes";

export const selectCurrentPeriod = (
  periods: EfteruddannelsesperiodeIndberetningOverviewModel[] | undefined,
): EfteruddannelsesperiodeIndberetningOverviewModel | undefined =>
  periods?.find((period) => period.erNuvaerendePeriode);

type CurrentPeriodLessonsStatusInput = {
  /** Summen fra CLE: godkendt dokumentation, der endnu ikke er indberettet. */
  availableLessons: number | null | undefined;
  /** False naar CLE ikke kunne svare. */
  lessonsKnown: boolean;
  periodFromDate: string;
  /** Visningsdatoen — periodens sidste dag, ikke den eksklusive slutdato. */
  periodToDateMinusOneDay: string;
  requiredLessons?: number;
  /** Lektioner overfoert IND i perioden fra den foregaaende. Kommer fra AIKA, ikke CLE. */
  transferredLessons?: number;
};

export type CurrentPeriodLessonsStatus = {
  /** "38/54 lektioner" — figuren paa forsiden. */
  figureText: string;
  /** "38 / 54" — vaerdien i metrics-feltet paa detaljesiden. */
  detailValueText: string;
  missingLessons: number;
  /** "1. september 2026 - 31. august 2029 · 16 lektioner mangler" */
  rangeLine: string;
  /**
   * "heraf 10 lektioner overfoert fra sidste periode", eller null naar der ikke er
   * overfoert noget. Saa kan advokaten se, at overfoerslen rent faktisk er sket.
   */
  transferredLine: string | null;
  requirementMet: boolean;
};

/**
 * Bygger de tekster, der viser status for en periode.
 *
 * Optalte lektioner er godkendt dokumentation PLUS det, der er overfoert ind i perioden —
 * samme regnestykke som ReportStatusBanner (`krav - overfoert - godkendte`), der bruges,
 * naar indberetningen aabner. Tælles overfoerte lektioner ikke med, viser den samme periode
 * to forskellige tal foer og efter, at indberetningen aabnede.
 *
 * Tælleren klampes med vilje ikke: har man 60 af 54 lektioner, er "60/54" aerligt, mens
 * "54/54" ville skjule det overskud, indberetningen senere lader en overfoere.
 */
export const buildCurrentPeriodLessonsStatus = ({
  availableLessons,
  lessonsKnown,
  periodFromDate,
  periodToDateMinusOneDay,
  requiredLessons = REQUIRED_PERIOD_LESSONS,
  transferredLessons = 0,
}: CurrentPeriodLessonsStatusInput): CurrentPeriodLessonsStatus => {
  const periodRange = `${formatDanishLongDate(
    periodFromDate,
    "-",
  )} - ${formatDanishLongDate(periodToDateMinusOneDay, "-")}`;

  const transferredLine =
    transferredLessons > 0
      ? `heraf ${formatLessons(
          transferredLessons,
        )} lektioner overført fra sidste periode`
      : null;

  if (!lessonsKnown || availableLessons == null) {
    return {
      figureText: LESSONS_UNAVAILABLE_LABEL,
      detailValueText: LESSONS_UNAVAILABLE_LABEL,
      missingLessons: requiredLessons,
      rangeLine: periodRange,
      transferredLine,
      requirementMet: false,
    };
  }

  const countedLessons = availableLessons + transferredLessons;
  const missingLessons = Math.max(requiredLessons - countedLessons, 0);
  const requirementMet = missingLessons === 0;
  const formattedCounted = formatLessons(countedLessons);
  const formattedRequired = formatLessons(requiredLessons);

  return {
    figureText: `${formattedCounted}/${formattedRequired} lektioner`,
    detailValueText: `${formattedCounted} / ${formattedRequired}`,
    missingLessons,
    // Aldrig "0 lektioner mangler" — sig at kravet er opfyldt i stedet.
    rangeLine: `${periodRange} · ${
      requirementMet
        ? "kravet er opfyldt"
        : `${formatLessons(missingLessons)} lektioner mangler`
    }`,
    transferredLine,
    requirementMet,
  };
};
